// Formatting: money, time, phone numbers. Everything goes through Intl — the
// user's locale decides separators; the wire stays integers and ISO strings.

import { currentLanguage } from '$lib/i18n/index.svelte';
import type { Money } from '$lib/protocol/types';

// Language-independent formatters live in format-plain.ts so the service worker
// can use them without pulling this module's reactive i18n import in with them.
// Re-exported here so every existing call site keeps working unchanged.
export { formatBytes, formatPhone } from '$lib/format-plain';

/** Amounts travel as the smallest currency unit; Intl knows how many minor
 *  digits the currency has, so 1234 PLN-grosze → "12,34 zł" and 1234 JPY → "¥1,234". */
export function formatMoney(m: Money): string {
  const locale = currentLanguage();
  try {
    const probe = new Intl.NumberFormat(locale, { style: 'currency', currency: m.currency });
    const digits = probe.resolvedOptions().maximumFractionDigits ?? 2;
    return probe.format(m.amount / 10 ** digits);
  } catch {
    return `${(m.amount / 100).toFixed(2)} ${m.currency}`;
  }
}

export function formatTime(ms: number): string {
  return new Intl.DateTimeFormat(currentLanguage(), { hour: '2-digit', minute: '2-digit' }).format(
    new Date(ms),
  );
}

export function formatDate(ms: number): string {
  return new Intl.DateTimeFormat(currentLanguage(), {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(ms));
}

export function formatDateTime(date: Date): string {
  return new Intl.DateTimeFormat(currentLanguage(), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

/** The list separator for a conversation: time today, date otherwise. */
export function formatListTime(ms: number): string {
  const now = new Date();
  const then = new Date(ms);
  const sameDay =
    now.getFullYear() === then.getFullYear() &&
    now.getMonth() === then.getMonth() &&
    now.getDate() === then.getDate();
  return sameDay
    ? formatTime(ms)
    : new Intl.DateTimeFormat(currentLanguage(), { day: 'numeric', month: 'short' }).format(then);
}


